import React, { FC } from 'react'
import { Grid, Paper } from '@mui/material'
import { DataGridPremium, useGridApiRef, GridPaginationModel, GridColDef } from '@mui/x-data-grid-premium'
import { HasSidebarLayout, DataLoad, ListHeader, TableToolbar, UploadSensors } from 'src/components'
import { useSensorUploadListRequest } from 'src/hooks/api'
import { useQueryParams } from 'src/hooks/ui'
import { SensorUpload } from 'src/api'
import { SensorUploadColumnsDataGrid } from 'src/components/columns/sensorUpload'
import { SearchParamsKeys } from 'src/enums'
import { defaultRowsValuePerPage } from 'src/config'

const columns: GridColDef<SensorUpload>[] = [
  SensorUploadColumnsDataGrid.Id,
  SensorUploadColumnsDataGrid.FileName,
  SensorUploadColumnsDataGrid.Author,
  SensorUploadColumnsDataGrid.Status,
  SensorUploadColumnsDataGrid.CreatedCount,
  SensorUploadColumnsDataGrid.SkippedCount,
  SensorUploadColumnsDataGrid.ImportedAt
]

const defaultPerPage = 50
const defaultPage = 1
const initialQueryParams = {
  page: `${defaultPage}`,
  rowsPerPage: `${defaultPerPage}`,
  sort: 'createdAt:desc'
}

const SensorUploadList: FC = () => {
  document.title = 'CLīMIT Admin Panel - Sensors Uploading History'
  const apiRef = useGridApiRef()
  const [queryParams, setQueryParams] = useQueryParams(initialQueryParams)
  const { isInitialLoading, isError, data, error } = useSensorUploadListRequest({ params: queryParams })
  const uploads: SensorUpload[] = data?.rows || []
  const count: number = data?.count || 0

  const handleChangePaginationModel = async (pagination: GridPaginationModel): Promise<void> => {
    setQueryParams(
      {
        [SearchParamsKeys.Page]: `${pagination.page + 1}`,
        [SearchParamsKeys.RowsPerPage]: `${pagination.pageSize}`
      },
      [SearchParamsKeys.Page, SearchParamsKeys.RowsPerPage]
    )
  }

  const page = queryParams.page ? parseInt(queryParams.page, 10) - 1 : defaultPage - 1
  const pageSize = parseInt(queryParams.rowsPerPage, 10) || defaultPerPage

  return (
    <HasSidebarLayout>
      <DataLoad isLoading={false} isError={isError} errorMessage={error?.message}>
        <Grid container direction="column" alignItems="stretch">
          <ListHeader title="CLIMIT Sensors Uploading History">
            <UploadSensors />
          </ListHeader>
        </Grid>
        <Paper sx={{ flexGrow: 1, minHeight: '200px', width: '100%', mt: 2 }}>
          <DataGridPremium<SensorUpload>
            apiRef={apiRef}
            pagination
            disableRowSelectionOnClick
            rows={uploads}
            columns={columns}
            rowCount={count}
            loading={isInitialLoading}
            paginationModel={{ page, pageSize }}
            rowThreshold={2}
            columnThreshold={2}
            rowHeight={65}
            paginationMode="server"
            pageSizeOptions={defaultRowsValuePerPage}
            onPaginationModelChange={handleChangePaginationModel}
            slots={{ toolbar: TableToolbar }}
            sx={{ border: 'none' }}
          />
        </Paper>
      </DataLoad>
    </HasSidebarLayout>
  )
}

export default SensorUploadList
